import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Message } from '@patsnap/tahiti-core';
import type { ProjectShareService } from '../project-share-service';
import type {
  ProjectShareApi,
  SharedUser,
  SharedUserListResponse,
} from '../../apis/project-share-api';
import { ProjectShareApi as ProjectShareApiSymbol } from '../../apis/project-share-api';
import type { InviteProjectByEmailData, SharedUserListData } from '@/domain/types';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { ShareI18nService } from '../share-i18n-service';
import { accountDeferred } from '@eureka/utils/account';
import { ShareRole } from '@eureka/services/src/types/share';

@injectable()
export class ProjectShareServiceImpl implements ProjectShareService {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    @inject(ProjectShareApiSymbol) private readonly _api: ProjectShareApi,
    @inject(Message) private readonly _message: Message,
    @inject(ShareI18nService) private readonly _i18nService: ShareI18nService,
    @inject(BiTrackService) private readonly _biTrackService: BiTrackService,
  ) {}

  inviteProjectByEmail(data: InviteProjectByEmailData): Promise<void> {
    return this._api.shareByEmail(data).then(() => {
      this._message.success({
        message: this._i18nService.t('airShareDialog.emailSuccess'),
      });
      this._biTrackService.biTrack(EurekaTrackEventType.SHARE_INFO, {
        sourceType: 'invite-project-by-email',
        otherParam: data.from,
        eventDetailStr: {
          id: data.work_project_id,
          email: data.share_emails[0],
        },
      });
    });
  }

  async _getLoginUserAsProjectOwner(): Promise<SharedUser> {
    const accountInfo = await accountDeferred.promise;
    const { nickname, email, mobile_number } = accountInfo._info;
    return {
      share_id: '',
      user_email: email || nickname || mobile_number || '',
      share_type: ShareRole.OWNER,
      valid: true,
    };
  }

  async getSharedUsers(data: SharedUserListData): Promise<SharedUserListResponse> {
    const res = await this._api.getSharedUserList(data);
    const loginUser = await this._getLoginUserAsProjectOwner();
    if (data.page === 1) {
      res.result.unshift(loginUser);
    }
    return res;
  }

  removeUser(projectId: string, shareId: string): Promise<void> {
    return this._api
      .removeSharedUser({
        work_project_id: projectId,
        share_id: shareId,
      })
      .then(() => {
        this._message.success({
          message: this._i18nService.t('airShareDialog.removeUserSuccess'),
        });
      });
  }
}
